import { signInWithRedirect } from "aws-amplify/auth";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useAppState } from "../../contexts/App";
import { useQueryUrl } from "../../hooks/QueryUrl";
import { useSiteRef } from "../../hooks/SiteRef";
import * as authenticationService from "../../services/auth";
import { getMissingPasswordRequirementMessages, isPasswordRequirementMessage } from "../../utils/passwordRequirements";
import RegisterStep1Page from "./components/RegisterStep1";
import RegisterStep2Page from "./components/RegisterStep2";
import RegisterStep3Page from "./components/RegisterStep3";
import { RegisterData } from "./types";

const RESEND_CODE_COOLDOWN_SECONDS = 120;

const getSignUpErrorMessages = (message: string, password: string, t: (key: string) => string) => {
  const lowerCaseMessage = message.toLowerCase();

  if (isPasswordRequirementMessage(message)) {
    return getMissingPasswordRequirementMessages(password, t);
  }

  if (lowerCaseMessage.includes("auth_token_not_found")) {
    return [t("pages.register.errors.generic")];
  }
  if (
    lowerCaseMessage.includes("username exists") ||
    lowerCaseMessage.includes("email exists") ||
    lowerCaseMessage.includes("email_already_registered") ||
    lowerCaseMessage.includes("already registered") ||
    lowerCaseMessage.includes("already in use") ||
    lowerCaseMessage.includes("conflict")
  ) {
    return [t("pages.register.errors.emailAlreadyRegistered")];
  }
  if (lowerCaseMessage.includes("password")) {
    return getMissingPasswordRequirementMessages(password, t);
  }
  if (lowerCaseMessage.includes("code")) {
    return [t("pages.register.errors.invalidCode")];
  }
  return [t("pages.register.errors.generic")];
};

/**
 * The sign up itself (e-mail → name and password → code, or Google/Facebook), shared by /register and the
 * /comecar pages, which only change the look around it. `stepForm` is the form of the current step.
 */
export const useRegister = ({
  emailButtonLabel,
  emailButtonAlwaysOn = false,
}: { emailButtonLabel?: string; emailButtonAlwaysOn?: boolean } = {}) => {
  const { t } = useTranslation();
  const queryUrl = useQueryUrl();
  const { setRef } = useSiteRef();
  const { markAuthenticated } = useAppState();
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [data, setData] = useState<RegisterData>({
    email: "",
    name: "",
    password: "",
  });
  const [activationCode, setActivationCode] = useState("");
  const [isLoading, setLoading] = useState(false);
  // Only the "send the code again" request (the link's own text); isLoading covers every request
  const [isResending, setResending] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [resendCodeCooldownSeconds, setResendCodeCooldownSeconds] = useState(0);

  const startFederatedLogin = async (provider: "Google" | "Facebook") => {
    await signInWithRedirect({
      provider: provider,
    });
  };

  const startResendCodeCooldown = () => {
    setResendCodeCooldownSeconds(RESEND_CODE_COOLDOWN_SECONDS);
  };

  const signInAfterRegister = async () => {
    await authenticationService.signInWithEmailAndPassword(data.email, data.password);
    await markAuthenticated();
    navigate("/", { replace: true });
  };

  const checkEmailAndGoToNextStep = async () => {
    if (isLoading) {
      return;
    }
    if (!data.email) {
      if (emailButtonAlwaysOn) setErrorMessages([t("pages.register.errors.emailRequired")]);
      return;
    }

    setErrorMessages([]);
    setLoading(true);
    try {
      const nextStep = await authenticationService.checkEmail(data.email);

      if (nextStep === "register") {
        setStep(2);
        return;
      }

      if (nextStep === "login") {
        setErrorMessages([t("pages.register.errors.emailAlreadyRegistered")]);
        return;
      }

      if (nextStep === "verify_email") {
        await authenticationService.resendSignUpCode(data.email);
        setActivationCode("");
        startResendCodeCooldown();
        setStep(3);
      }
    } catch (e) {
      setErrorMessages(getSignUpErrorMessages((e as Error).message, data.password, t));
    } finally {
      setLoading(false);
    }
  };

  const signUpWithEmailAndPassword = async () => {
    const passwordErrors = getMissingPasswordRequirementMessages(data.password, t);
    if (passwordErrors.length > 0) {
      setErrorMessages(passwordErrors);
      return;
    }

    setErrorMessages([]);
    setLoading(true);
    try {
      const result = await authenticationService.signUpWithEmailAndPassword({
        email: data.email,
        name: data.name,
        password: data.password,
      });

      if (result?.requiresConfirmation === false || result?.nextStep?.signUpStep === "DONE") {
        await signInAfterRegister();
      } else {
        startResendCodeCooldown();
        setStep(3);
      }
    } catch (e) {
      setErrorMessages(getSignUpErrorMessages((e as Error).message, data.password, t));
    } finally {
      setLoading(false);
    }
  };

  const confirmCodeAndFinishSignUp = async () => {
    setErrorMessages([]);
    setLoading(true);

    try {
      await authenticationService.confirmSignUpCode(data.email, activationCode);
      await signInAfterRegister();
    } catch (e) {
      setErrorMessages(getSignUpErrorMessages((e as Error).message, data.password, t));
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    if (resendCodeCooldownSeconds > 0) {
      return;
    }

    setErrorMessages([]);
    setLoading(true);
    setResending(true);
    try {
      await authenticationService.resendSignUpCode(data.email);
      startResendCodeCooldown();
    } catch (e) {
      setErrorMessages(getSignUpErrorMessages((e as Error).message, data.password, t));
    } finally {
      setLoading(false);
      setResending(false);
    }
  };

  useEffect(() => {
    if (resendCodeCooldownSeconds <= 0) {
      return;
    }

    const timer = window.setInterval(() => {
      setResendCodeCooldownSeconds(currentSeconds => (currentSeconds > 0 ? currentSeconds - 1 : 0));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [resendCodeCooldownSeconds]);

  useEffect(() => {
    setRef(queryUrl.get("ref"));
  }, [queryUrl, setRef]);

  const stepForm =
    step === 1 ? (
      <RegisterStep1Page
        data={data}
        setData={setData}
        onNextStep={checkEmailAndGoToNextStep}
        loading={isLoading}
        buttonLabel={emailButtonLabel}
        alwaysEnabled={emailButtonAlwaysOn}
      />
    ) : step === 2 ? (
      <RegisterStep2Page data={data} setData={setData} onNextStep={signUpWithEmailAndPassword} loading={isLoading} />
    ) : (
      <RegisterStep3Page
        data={data}
        activationCode={activationCode}
        setActivationCode={setActivationCode}
        onNextStep={confirmCodeAndFinishSignUp}
        onPreviousStep={() => setStep(2)}
        loading={isLoading}
        resending={isResending}
        onResendCode={resendCode}
        resendCodeCooldownSeconds={resendCodeCooldownSeconds}
      />
    );

  // Back to the e-mail step (to fix a typo), with any message cleared
  const editEmail = () => {
    setErrorMessages([]);
    setStep(1);
  };

  return { step, email: data.email, errorMessages, stepForm, startFederatedLogin, editEmail };
};
